import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from '../../api/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const EXTRA_FIELDS = [
  { key: 'codigo', label: 'Código' },
  { key: 'num_serie', label: 'Nº Serie' },
  { key: 'modelo', label: 'Modelo' },
  { key: 'marca', label: 'Marca' },
];

const EMPTY = { name: '', capacidad: '', codigo: '', num_serie: '', modelo: '', marca: '' };

// Saved cámaras may lack the optional fields (null/undefined): inputs need strings
const toForm = (cam) => Object.fromEntries(Object.keys(EMPTY).map((k) => [k, cam[k] ?? '']));

const CamaraForm = ({ value, onChange, prefix }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 flex-1">
    <Input
      placeholder="Nombre de la cámara"
      value={value.name}
      onChange={(e) => onChange({ ...value, name: e.target.value })}
      data-testid={`${prefix}-name`}
    />
    <Input
      type="number"
      min="0"
      placeholder="Capacidad (unidades)"
      value={value.capacidad}
      onChange={(e) => onChange({ ...value, capacidad: e.target.value })}
      data-testid={`${prefix}-capacidad`}
    />
    {EXTRA_FIELDS.map(({ key, label }) => (
      <Input
        key={key}
        placeholder={label}
        value={value[key]}
        onChange={(e) => onChange({ ...value, [key]: e.target.value })}
        data-testid={`${prefix}-${key}`}
      />
    ))}
  </div>
);

const isValid = (c) => c.name.trim() && c.capacidad !== '' && parseInt(c.capacidad, 10) >= 0;

const CamarasTab = () => {
  const [camaras, setCamaras] = useState([]);
  const [nueva, setNueva] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const fetchCamaras = async () => {
    const res = await axios.get('/camaras');
    setCamaras(res.data);
  };

  useEffect(() => { fetchCamaras(); }, []);

  const save = async (data, id) => {
    if (!isValid(data)) return toast.error('Indica nombre y capacidad válidos');
    const body = { ...toForm(data), capacidad: parseInt(data.capacidad, 10) };
    try {
      if (id) await axios.put(`/camaras/${id}`, body);
      else await axios.post('/camaras', body);
      toast.success(id ? 'Cámara actualizada' : 'Cámara creada');
      id ? setEditing(null) : setNueva(EMPTY);
      fetchCamaras();
    } catch {
      toast.error('Error al guardar la cámara');
    }
  };

  const remove = async (cam) => {
    if (!window.confirm(`¿Eliminar la cámara "${cam.name}"? Se quitará de las muestras asignadas.`)) return;
    try {
      await axios.delete(`/camaras/${cam.id}`);
      toast.success('Cámara eliminada');
      fetchCamaras();
    } catch {
      toast.error('Error al eliminar la cámara');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Gestión de Cámaras Climáticas</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="border p-4 rounded-lg bg-blue-50 flex flex-col md:flex-row gap-2">
          <CamaraForm value={nueva} onChange={setNueva} prefix="new-camara" />
          <Button onClick={() => save(nueva)} data-testid="add-camara-btn">
            <Plus size={16} className="mr-1" /> Añadir
          </Button>
        </div>
        <div className="space-y-2">
          {camaras.length === 0 && <p className="text-sm text-gray-500">No hay cámaras creadas.</p>}
          {camaras.map((cam) => (
            <div key={cam.id} className="border p-3 rounded-lg flex items-start md:items-center gap-2" data-testid={`camara-${cam.id}`}>
              {editing?.id === cam.id ? (
                <>
                  <CamaraForm value={editing} onChange={setEditing} prefix="edit-camara" />
                  <Button size="sm" onClick={() => save(editing, cam.id)} data-testid="update-camara-btn"><Save size={16} /></Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(null)}><X size={16} /></Button>
                </>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold">{cam.name}</span>
                    {EXTRA_FIELDS.some(({ key }) => cam[key]) && (
                      <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5" data-testid={`camara-datos-${cam.id}`}>
                        {EXTRA_FIELDS.filter(({ key }) => cam[key]).map(({ key, label }) => (
                          <span key={key}>{label}: <span className="text-gray-700">{cam[key]}</span></span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="text-sm text-gray-600" data-testid={`camara-capacidad-${cam.id}`}>Capacidad: {cam.capacidad}</span>
                  <Button size="sm" variant="outline" onClick={() => setEditing({ id: cam.id, ...toForm(cam) })} data-testid={`edit-camara-btn-${cam.id}`}><Edit2 size={16} /></Button>
                  <Button size="sm" variant="destructive" onClick={() => remove(cam)} data-testid={`delete-camara-btn-${cam.id}`}><Trash2 size={16} /></Button>
                </>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default CamarasTab;
